import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search } from 'lucide-react';
import { API } from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import { formatDateTime } from '../lib/brand';

const STATUS_FILTERS = [
  { key: '', label: 'Alle' },
  { key: 'neu', label: 'Neu' },
  { key: 'in_bearbeitung', label: 'In Bearbeitung' },
  { key: 'reparatur', label: 'Reparatur' },
  { key: 'fertig', label: 'Fertig' },
];

export default function AdminCases() {
  const [params, setParams] = useSearchParams();
  const initialStatus = params.get('status') || '';
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState('');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = {};
      if (status) q.status = status;
      if (search) q.search = search;
      const { data } = await API.get('/admin/cases', { params: q });
      setCases(data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="space-y-4" data-testid="admin-cases">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-semibold text-2xl text-[hsl(var(--primary))]">Schadenfälle</h1>
          <p className="text-sm text-slate-600">Alle gemeldeten Fälle.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || 'all'}
            onClick={() => { setStatus(f.key); if (f.key) params.set('status', f.key); else params.delete('status'); setParams(params); }}
            className={`px-3 py-1.5 rounded-xl border text-sm ${status === f.key ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'bg-white text-slate-800 border-slate-200 hover:bg-slate-50'}`}
            data-testid={`admin-cases-filter-${f.key || 'all'}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suche: Fallnummer, Kunde, Kennzeichen" className="h-10 rounded-xl w-72" data-testid="admin-cases-search-input" />
          <Button onClick={load} variant="outline" data-testid="admin-cases-search-button"><Search className="h-4 w-4" /></Button>
        </div>
      </div>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-cases-table">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-600">
                  <th className="px-4 py-3">Fallnummer</th>
                  <th className="px-4 py-3">Kunde</th>
                  <th className="px-4 py-3">Fahrzeug</th>
                  <th className="px-4 py-3">Kennzeichen</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Erstellt</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-6 text-slate-500">Lade...</td></tr>
                ) : cases.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-6 text-slate-500">Keine Fälle gefunden.</td></tr>
                ) : cases.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{c.case_number}{c.is_emergency && <span className="ml-1 text-red-600">!</span>}</td>
                    <td className="px-4 py-3">{c.person.first_name} {c.person.last_name}</td>
                    <td className="px-4 py-3">{c.vehicle.make} {c.vehicle.model}</td>
                    <td className="px-4 py-3">{c.vehicle.license_plate}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3 text-slate-500">{formatDateTime(c.created_at)}</td>
                    <td className="px-4 py-3 text-right"><Link to={`/admin/schadenfaelle/${c.id}`} className="text-[hsl(var(--primary))] font-medium" data-testid={`admin-cases-open-${c.id}`}>Öffnen</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
